/**
 * useSpeakerMemories Hooks
 *
 * React Query hooks for managing speaker memories.
 * Provides read, update, reset, and regenerate operations with caching.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  clearSpeakerMemory,
  clearUserSelfMemory,
  getSpeakerMemory,
  getUserSelfMemory,
  listSpeakerMemories,
  regenerateSpeakerMemory,
  regenerateUserSelfMemory,
  resetSpeakerMemory,
  resetUserSelfMemory,
  updateSpeakerMemory,
  updateUserSelfMemory,
  validateText,
} from "../services/speakerMemories";
import type { TextValidationResult } from "../services/speakerMemories";
import type {
  SpeakerMemory,
  SpeakerMemoryListResponse,
} from "../types/speakerMemory";

const LIST_KEY = ["speakerMemories"] as const;
const detailKey = (partnerId: string) => ["speakerMemory", partnerId] as const;

/**
 * Fetch all speaker memories for the current user.
 */
export function useSpeakerMemories() {
  return useQuery<SpeakerMemoryListResponse, Error>({
    queryKey: LIST_KEY,
    queryFn: listSpeakerMemories,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Fetch a single speaker memory by partner ID.
 *
 * While the latest fetched memory has `regeneration_in_progress=true` the
 * query auto-refetches every 2s so the UI sees the new content land as
 * soon as the worker finishes.
 */
export function useSpeakerMemory(partnerId: string | undefined) {
  return useQuery<SpeakerMemory, Error>({
    queryKey: detailKey(partnerId || ""),
    queryFn: () => getSpeakerMemory(partnerId!),
    enabled: !!partnerId,
    staleTime: 5 * 60 * 1000,
    refetchInterval: (query) =>
      query.state.data?.regeneration_in_progress ? 2000 : false,
    retry: (failureCount, error) => {
      if (error.message?.includes("404")) return false;
      return failureCount < 2;
    },
  });
}

/**
 * Update a speaker memory's content.
 */
export function useUpdateSpeakerMemory() {
  const queryClient = useQueryClient();

  return useMutation<
    SpeakerMemory,
    unknown,
    { partnerId: string; content: string; force?: boolean }
  >({
    mutationFn: ({ partnerId, content, force }) =>
      updateSpeakerMemory(partnerId, content, force),
    onSuccess: (updatedMemory, { partnerId }) => {
      queryClient.setQueryData(detailKey(partnerId), updatedMemory);
      queryClient.invalidateQueries({ queryKey: LIST_KEY });
    },
  });
}

/**
 * Reset a speaker memory to the AI version.
 */
export function useResetSpeakerMemory() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: resetSpeakerMemory,
    onSuccess: (_, partnerId) => {
      queryClient.invalidateQueries({ queryKey: detailKey(partnerId) });
      queryClient.invalidateQueries({ queryKey: LIST_KEY });
    },
  });
}

/**
 * Manually trigger async regeneration of a speaker memory.
 *
 * The backend returns 202 immediately; the new memory will land via the
 * polling loop in `useSpeakerMemory` once the worker finishes. We seed
 * the cache with the in-progress row so the UI can show a spinner right
 * away without waiting for the next refetch.
 */
export function useRegenerateSpeakerMemory() {
  const queryClient = useQueryClient();

  return useMutation<SpeakerMemory, Error, string>({
    mutationFn: regenerateSpeakerMemory,
    onSuccess: (inProgressMemory, partnerId) => {
      queryClient.setQueryData(detailKey(partnerId), inProgressMemory);
      queryClient.invalidateQueries({ queryKey: LIST_KEY });
    },
  });
}

// --- User Self-Memory Hooks ---

const SELF_KEY = ["selfMemory"] as const;

/**
 * Fetch the user's self-memory.
 *
 * While `regeneration_in_progress=true` the query auto-refetches every 2s
 * so the UI sees the new content land as soon as the worker finishes.
 */
export function useUserSelfMemory() {
  return useQuery<SpeakerMemory, Error>({
    queryKey: SELF_KEY,
    queryFn: getUserSelfMemory,
    staleTime: 5 * 60 * 1000,
    refetchInterval: (query) =>
      query.state.data?.regeneration_in_progress ? 2000 : false,
    retry: (failureCount, error) => {
      if (error.message?.includes("404")) return false;
      return failureCount < 2;
    },
  });
}

/**
 * Update the user's self-memory content.
 */
export function useUpdateUserSelfMemory() {
  const queryClient = useQueryClient();

  return useMutation<
    SpeakerMemory,
    unknown,
    { content: string; force?: boolean }
  >({
    mutationFn: ({ content, force }) => updateUserSelfMemory(content, force),
    onSuccess: (updatedMemory) => {
      queryClient.setQueryData(SELF_KEY, updatedMemory);
    },
  });
}

/**
 * Reset the user's self-memory to the AI version.
 */
export function useResetUserSelfMemory() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, void>({
    mutationFn: resetUserSelfMemory,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SELF_KEY });
    },
  });
}

/**
 * Manually trigger async regeneration of the user's self-memory.
 *
 * The backend returns 202 immediately; the new memory will land via the
 * polling loop in `useUserSelfMemory` once the worker finishes.
 */
export function useRegenerateUserSelfMemory() {
  const queryClient = useQueryClient();

  return useMutation<SpeakerMemory, Error, void>({
    mutationFn: regenerateUserSelfMemory,
    onSuccess: (inProgressMemory) => {
      queryClient.setQueryData(SELF_KEY, inProgressMemory);
    },
  });
}

// --- Clear Memory Hooks ---

/**
 * Clear a partner memory so it regenerates fresh after next reflection.
 */
export function useClearSpeakerMemory() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: clearSpeakerMemory,
    onSuccess: (_, partnerId) => {
      queryClient.invalidateQueries({ queryKey: detailKey(partnerId) });
      queryClient.invalidateQueries({ queryKey: LIST_KEY });
    },
  });
}

/**
 * Clear the user's self-memory so it regenerates fresh after next reflection.
 */
export function useClearUserSelfMemory() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, void>({
    mutationFn: clearUserSelfMemory,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SELF_KEY });
    },
  });
}

// --- Text Validation Hook ---

/**
 * Validate text for bias / unfair characterizations.
 */
export function useValidateText() {
  return useMutation<
    TextValidationResult,
    Error,
    {
      text: string;
      context?:
        | "relationship_description"
        | "reflection_context"
        | "memory_edit";
    }
  >({
    mutationFn: ({ text, context }) => validateText(text, context),
  });
}
